import React, { useEffect, useState } from "react";
import {
  Table,
  Card,
  Tag,
  Avatar,
  Typography,
  Modal,
  Descriptions,
  Divider,
  Button,
  Space,
  Row,
  Col,
  Statistic,
  Empty,
  Input,
} from "antd";
import {
  GiftOutlined,
  ShopOutlined,
  EyeOutlined,
  StarOutlined,
  MoneyCollectOutlined,
  DollarOutlined,
  LinkOutlined,
  UserOutlined,
  InstagramOutlined,
  ClockCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import moment from "moment";
import "moment/locale/fr";

moment.locale("fr");

const { Title, Text } = Typography;

const TIER_DEFINITIONS = [
  {
    type: "Starter",
    trendPrize: "Starter",
    categoryKey: "starterCategory",
    conditionKey: "starterCondition",
    conditionValueKey: "starterConditionValue",
  },
  {
    type: "Trendy",
    trendPrize: "Trendy",
    categoryKey: "trendyCategory",
    conditionKey: "trendyCondition",
    conditionValueKey: "trendyConditionValue",
  },
  {
    type: "Pro",
    trendPrize: "Pro",
    categoryKey: "proCategory",
    conditionKey: "proCondition",
    conditionValueKey: "proConditionValue",
  },
  {
    type: "Superstar",
    trendPrize: "Superstar",
    categoryKey: "superStarCategory",
    conditionKey: "superStarCondition",
    conditionValueKey: "superStarConditionValue",
  },
];

const NATURE_LABELS = {
  produit: "Produit Offert",
  "produit offert": "Produit Offert",
  service: "Service Offert",
  "service offert": "Service Offert",
  reduction: "Bon de reduction",
  "bon de reduction": "Bon de reduction",
  "bon d'achat": "Bon d'achat",
  "bon dachat": "Bon d'achat",
};

const toArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);

const safeNumber = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const getEtabName = (etablissement) =>
  etablissement?.nameEtab ||
  etablissement?.title ||
  etablissement?.name ||
  "Etablissement";

const getEtabId = (etablissement) =>
  String(etablissement?._id || etablissement?.id || getEtabName(etablissement));

const getImageUrl = (avatar) =>
  avatar ? `${process.env.REACT_APP_API_URL}/upload/${avatar}` : undefined;

const normalizeConditionValue = (value) => {
  if (Array.isArray(value)) {
    const normalizedValues = value
      .map((item) => String(item ?? "").trim())
      .filter(Boolean);

    return normalizedValues.length ? normalizedValues.join(", ") : "aucune";
  }

  const normalizedValue = String(value ?? "").trim();
  return normalizedValue || "aucune";
};

const formatRewardNature = (value) => {
  const normalizedValue = String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  if (!normalizedValue) {
    return "Produit Offert";
  }

  return NATURE_LABELS[normalizedValue] || value;
};

const formatRewardType = (value) => {
  const normalizedValue = String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  if (normalizedValue.includes("imm")) {
    return "Immediate";
  }

  if (normalizedValue.includes("starter")) {
    return "Starter";
  }

  if (normalizedValue.includes("trendy")) {
    return "Trendy";
  }

  if (normalizedValue === "pro") {
    return "Pro";
  }

  if (normalizedValue.includes("super")) {
    return "Superstar";
  }

  return value || "NA";
};

const formatTrendPrize = (value) => {
  const normalizedValue = String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  if (!normalizedValue || normalizedValue === "na") {
    return "NA";
  }

  return formatRewardType(value);
};

const formatCondition = (value) => {
  const normalizedValue = normalizeConditionValue(value);
  return normalizedValue.toLowerCase() === "aucune" ? "Aucune" : normalizedValue;
};

const buildRewardKey = (reward) =>
  [
    reward.etabId,
    reward.campaignName,
    reward.type,
    reward.trendPrize || "NA",
    reward.title,
    safeNumber(reward.value),
    safeNumber(reward.cout),
  ].join("::");

const buildUsageDelayLabel = (reward) => {
  const usageDelay = safeNumber(reward.usageDelay);

  if (usageDelay <= 0) {
    return "Immediate";
  }

  if (String(reward.type).toLowerCase().includes("imm")) {
    return `Apres ${usageDelay} heure${usageDelay > 1 ? "s" : ""}`;
  }

  return `Apres ${usageDelay} jour${usageDelay > 1 ? "s" : ""}`;
};

const transformRewardItem = (reward) => {
  const post = reward?.postId || {};
  const etablissement = post?.etablissement || {};
  const user = post?.userId || {};
  const nature = formatRewardNature(reward?.nature || reward?.recompenseCategory);

  const groupShape = {
    key: "",
    etabId: getEtabId(etablissement),
    etabName: getEtabName(etablissement),
    etabLogo: getImageUrl(etablissement?.avatar),
    etabLocation: etablissement?.location || "—",
    etabDescription: etablissement?.description || "—",
    etabPhone: etablissement?.phone || "—",
    campaignName: post?.selectedCampaignName || "Campagne inconnue",
    title: reward?.title || "Recompense",
    nature,
    type: formatRewardType(reward?.type),
    trendPrize: formatTrendPrize(reward?.trendPrize),
    value: safeNumber(reward?.value),
    cout: safeNumber(reward?.cout),
    usageDelay: reward?.usageDelay || "0",
    minPurchase: normalizeConditionValue(reward?.minPurchase),
  };

  return {
    ...groupShape,
    key: buildRewardKey(groupShape),
    status: reward?.status || "ready",
    rating: safeNumber(reward?.rating),
    item: {
      _id: reward?._id,
      status: reward?.status || "ready",
      title: reward?.title || "Recompense",
      value: safeNumber(reward?.value),
      cout: safeNumber(reward?.cout),
      dateCreation: reward?.dateCreation || null,
      dateUse: reward?.dateUse || null,
      dateExp: reward?.dateExp || null,
      etabLogo: getImageUrl(etablissement?.avatar),
      etabName: getEtabName(etablissement),
      etabLocation: etablissement?.location || "—",
      appUsername: user?.username || "—",
      instagramUsername: user?.usernameInsta || post?.username || "—",
      postUrl: post?.url || "",
    },
  };
};

const buildCatalogRewards = (etablissements) => {
  const rewards = [];

  for (const etablissement of toArray(etablissements)) {
    const campagnes = toArray(etablissement?.campagnes);

    for (const campagne of campagnes) {
      const sharedFields = {
        etabId: getEtabId(etablissement),
        etabName: getEtabName(etablissement),
        etabLogo: getImageUrl(etablissement?.avatar),
        etabLocation: etablissement?.location || "—",
        etabDescription: etablissement?.description || "—",
        etabPhone: etablissement?.phone || "—",
        campaignName: campagne?.name || "Campagne inconnue",
      };

      if (String(campagne?.recomponseImmediate ?? "").trim()) {
        const immediateReward = {
          ...sharedFields,
          title: campagne.recomponseImmediate,
          nature: formatRewardNature(
            campagne?.recomponseImmediateCategory || "produit offert"
          ),
          type: "Immediate",
          trendPrize: "NA",
          value: safeNumber(campagne?.recomponseImmediateValue),
          cout: safeNumber(campagne?.recomponseImmediateRealValue),
          usageDelay: campagne?.recomponseImmediatenbOfDaysforAvailability || "0",
          minPurchase: normalizeConditionValue(
            campagne?.recomponseImmediateMinPurchase
          ),
          distributionCount: 0,
          usageCount: 0,
          avgRating: 0,
          items: [],
        };

        immediateReward.key = buildRewardKey(immediateReward);
        rewards.push(immediateReward);
      }

      const recompenseOption = campagne?.recompenseOption || {};

      for (const tierDefinition of TIER_DEFINITIONS) {
        const categoryItems = toArray(recompenseOption[tierDefinition.categoryKey]);

        for (const categoryItem of categoryItems) {
          const rewardDefinition = {
            ...sharedFields,
            title: categoryItem?.name || "Recompense",
            nature: formatRewardNature(categoryItem?.category),
            type: tierDefinition.type,
            trendPrize: tierDefinition.trendPrize,
            value: safeNumber(categoryItem?.value),
            cout: safeNumber(categoryItem?.realValue),
            usageDelay: recompenseOption?.nbOfDaystoBeValdiated || "0",
            minPurchase:
              String(categoryItem?.[tierDefinition.conditionKey] || "").toLowerCase() ===
              "aucune"
                ? "aucune"
                : normalizeConditionValue(
                    categoryItem?.[tierDefinition.conditionValueKey]
                  ),
            distributionCount: 0,
            usageCount: 0,
            avgRating: 0,
            items: [],
          };

          rewardDefinition.key = buildRewardKey(rewardDefinition);
          rewards.push(rewardDefinition);
        }
      }
    }
  }

  return rewards;
};

const mergeRewardDefinitions = (catalogRewards, actualRewardItems) => {
  const rewardMap = new Map();

  catalogRewards.forEach((reward) => {
    rewardMap.set(reward.key, {
      ...reward,
      distributionCount: reward.distributionCount || 0,
      usageCount: reward.usageCount || 0,
      avgRating: reward.avgRating || 0,
      items: reward.items || [],
      ratingSum: 0,
      ratingCount: 0,
    });
  });

  actualRewardItems.forEach((rewardItem) => {
    const existingReward = rewardMap.get(rewardItem.key);

    if (!existingReward) {
      rewardMap.set(rewardItem.key, {
        ...rewardItem,
        distributionCount: 1,
        usageCount: rewardItem.status?.toLowerCase() === "used" ? 1 : 0,
        avgRating: rewardItem.rating || 0,
        items: [rewardItem.item],
        ratingSum: rewardItem.rating || 0,
        ratingCount: rewardItem.rating ? 1 : 0,
      });
      return;
    }

    existingReward.distributionCount += 1;
    existingReward.usageCount += rewardItem.status?.toLowerCase() === "used" ? 1 : 0;
    existingReward.items.push(rewardItem.item);

    if (rewardItem.rating > 0) {
      existingReward.ratingSum += rewardItem.rating;
      existingReward.ratingCount += 1;
    }

    if (
      (!existingReward.minPurchase ||
        String(existingReward.minPurchase).toLowerCase() === "aucune") &&
      rewardItem.minPurchase &&
      String(rewardItem.minPurchase).toLowerCase() !== "aucune"
    ) {
      existingReward.minPurchase = rewardItem.minPurchase;
    }

    if (
      (!existingReward.nature || existingReward.nature === "Produit Offert") &&
      rewardItem.nature
    ) {
      existingReward.nature = rewardItem.nature;
    }
  });

  return Array.from(rewardMap.values())
    .map((reward) => ({
      ...reward,
      avgRating: reward.ratingCount
        ? reward.ratingSum / reward.ratingCount
        : reward.avgRating || 0,
      items: reward.items.sort((leftItem, rightItem) => {
        const leftDate = leftItem?.dateCreation
          ? new Date(leftItem.dateCreation).getTime()
          : 0;
        const rightDate = rightItem?.dateCreation
          ? new Date(rightItem.dateCreation).getTime()
          : 0;

        return rightDate - leftDate;
      }),
    }))
    .sort((leftReward, rightReward) => {
      if (leftReward.etabName !== rightReward.etabName) {
        return leftReward.etabName.localeCompare(rightReward.etabName);
      }

      if (leftReward.campaignName !== rightReward.campaignName) {
        return leftReward.campaignName.localeCompare(rightReward.campaignName);
      }

      if (leftReward.type !== rightReward.type) {
        return leftReward.type.localeCompare(rightReward.type);
      }

      return leftReward.title.localeCompare(rightReward.title);
    });
};

const getStatusInfo = (status) => {
  switch (String(status ?? "").toLowerCase()) {
    case "ready":
      return { color: "green", text: "Disponible" };
    case "used":
      return { color: "purple", text: "Utilisee" };
    case "expired":
      return { color: "red", text: "Expiree" };
    default:
      return { color: "default", text: status || "—" };
  }
};

const RewardDetailModal = ({ visible, reward, onClose }) => {
  if (!reward) {
    return null;
  }

  const totalDistributed = reward.distributionCount * safeNumber(reward.value);
  const totalCost = reward.distributionCount * safeNumber(reward.cout);

  const itemColumns = [
    {
      title: "Etat",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (value) => {
        const statusInfo = getStatusInfo(value);
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
    {
      title: "Utilisateur App",
      dataIndex: "appUsername",
      key: "appUsername",
      width: 170,
      render: (value) => (
        <Space>
          <UserOutlined />
          <Text>{value || "—"}</Text>
        </Space>
      ),
    },
    {
      title: "Instagram",
      dataIndex: "instagramUsername",
      key: "instagramUsername",
      width: 170,
      render: (value) => (
        <Space>
          <InstagramOutlined />
          <Text>{value || "—"}</Text>
        </Space>
      ),
    },
    {
      title: "Post Instagram",
      dataIndex: "postUrl",
      key: "postUrl",
      width: 180,
      render: (value) =>
        value ? (
          <a href={value} target="_blank" rel="noopener noreferrer">
            <Space>
              <LinkOutlined />
              Voir le post
            </Space>
          </a>
        ) : (
          "—"
        ),
    },
    {
      title: "Titre",
      dataIndex: "title",
      key: "title",
      width: 220,
      render: (value, record) => (
        <Space>
          <Avatar size="small" src={record.etabLogo} icon={<ShopOutlined />} />
          <Text>{value}</Text>
        </Space>
      ),
    },
    {
      title: "Valeur",
      dataIndex: "value",
      key: "value",
      width: 110,
      align: "center",
      render: (value) => (
        <Tag color="green" icon={<MoneyCollectOutlined />}>
          {safeNumber(value)}EUR
        </Tag>
      ),
    },
    {
      title: "Cout",
      dataIndex: "cout",
      key: "cout",
      width: 110,
      align: "center",
      render: (value) => (
        <Tag color="orange" icon={<DollarOutlined />}>
          {safeNumber(value)}EUR
        </Tag>
      ),
    },
    {
      title: "Creee le",
      dataIndex: "dateCreation",
      key: "dateCreation",
      width: 160,
      render: (value) =>
        value ? moment(value).format("DD/MM/YYYY HH:mm") : "—",
    },
    {
      title: "Utilisee le",
      dataIndex: "dateUse",
      key: "dateUse",
      width: 160,
      render: (value) =>
        value ? moment(value).format("DD/MM/YYYY HH:mm") : "—",
    },
    {
      title: "Expire le",
      dataIndex: "dateExp",
      key: "dateExp",
      width: 160,
      render: (value) =>
        value ? moment(value).format("DD/MM/YYYY HH:mm") : "—",
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <GiftOutlined style={{ color: "#1890ff" }} />
          <span>Details de la recompense</span>
        </Space>
      }
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Fermer
        </Button>,
      ]}
      width={1200}
    >
      <Title level={4} style={{ margin: 0 }}>
        {reward.title}
      </Title>

      <Divider orientation="left">Resume</Divider>
      <Descriptions bordered column={2}>
        <Descriptions.Item label="Etablissement">
          {reward.etabName}
        </Descriptions.Item>
        <Descriptions.Item label="Campagne">
          {reward.campaignName}
        </Descriptions.Item>
        <Descriptions.Item label="Nature">
          <Tag>{reward.nature}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Type">
          <Tag color="blue">{reward.type}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Utilisation">
          <Space>
            <ClockCircleOutlined />
            <Text>{buildUsageDelayLabel(reward)}</Text>
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="Condition d'attribution">
          {formatCondition(reward.minPurchase)}
        </Descriptions.Item>
        <Descriptions.Item label="Valeur">
          <Tag color="green" icon={<MoneyCollectOutlined />}>
            {safeNumber(reward.value)}EUR
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Cout">
          <Tag color="orange" icon={<DollarOutlined />}>
            {safeNumber(reward.cout)}EUR
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Nombre distribution">
          {reward.distributionCount}
        </Descriptions.Item>
        <Descriptions.Item label="Nombre utilisation">
          {reward.usageCount}
        </Descriptions.Item>
        <Descriptions.Item label="Montant total distribue">
          <Text strong>{safeNumber(totalDistributed).toFixed(2)}EUR</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Cout total">
          <Text strong>{safeNumber(totalCost).toFixed(2)}EUR</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Evaluation moyenne">
          <Space>
            <StarOutlined style={{ color: "#fadb14" }} />
            <Text>{safeNumber(reward.avgRating).toFixed(1)}</Text>
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="Recompenses unitaires">
          {reward.items.length}
        </Descriptions.Item>
      </Descriptions>

      <Divider orientation="left">Recompenses unitaires</Divider>
      <Table
        size="small"
        columns={itemColumns}
        dataSource={reward.items}
        rowKey={(item) => item._id || `${item.title}-${item.dateCreation}`}
        scroll={{ x: "max-content" }}
        pagination={{ pageSize: 8, showSizeChanger: false }}
        locale={{ emptyText: "Aucune recompense unitaire pour le moment" }}
      />
    </Modal>
  );
};

const RewardDashboard = () => {
  const [groupedRewards, setGroupedRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReward, setSelectedReward] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const user = JSON.parse(localStorage.getItem("user") || '{"type":"admin"}');

  useEffect(() => {
    const fetchRewards = async () => {
      setLoading(true);

      try {
        const rewardsUrl =
          user.type === "admin"
            ? `${process.env.REACT_APP_API_URL}/rewards`
            : `${process.env.REACT_APP_API_URL}/rewards/ettab/${user?.IdEtab}`;
        const etablissementsUrl =
          user.type === "admin"
            ? `${process.env.REACT_APP_API_URL}/api/Etablissement`
            : `${process.env.REACT_APP_API_URL}/api/Etablissement/${user?.IdEtab}`;

        const [rewardsResponse, etablissementsResponse] = await Promise.all([
          fetch(rewardsUrl),
          fetch(etablissementsUrl),
        ]);

        const rewardsPayload = rewardsResponse.ok
          ? await rewardsResponse.json()
          : [];
        const etablissementsPayload = etablissementsResponse.ok
          ? await etablissementsResponse.json()
          : [];

        const actualRewardItems = toArray(rewardsPayload)
          .map(transformRewardItem)
          .filter(Boolean);
        const catalogRewards = buildCatalogRewards(toArray(etablissementsPayload));
        const mergedRewards = mergeRewardDefinitions(
          catalogRewards,
          actualRewardItems
        );

        setGroupedRewards(mergedRewards);
      } catch (error) {
        console.error("Erreur lors de la recuperation des recompenses:", error);
        setGroupedRewards([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRewards();
  }, [user?.IdEtab, user?.type]);

  const getSearchFilterProps = (dataIndex, placeholder) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={placeholder}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Rechercher
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reinitialiser
          </Button>
        </Space>
      </div>
    ),
    onFilter: (value, record) =>
      String(record[dataIndex] || "").toLowerCase().includes(value.toLowerCase()),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
  });

  const uniqueNatures = [...new Set(groupedRewards.map((r) => r.nature).filter(Boolean))];
  const uniqueTypes = [...new Set(groupedRewards.map((r) => r.type).filter(Boolean))];

  const columns = [
    {
      title: "Nom recompense",
      dataIndex: "title",
      key: "title",
      width: 240,
      sorter: (a, b) => a.title.localeCompare(b.title),
      ...getSearchFilterProps("title", "Rechercher par nom"),
      render: (value, record) => (
        <Space>
          <Avatar src={record.etabLogo} icon={<ShopOutlined />} />
          <Text strong>{value}</Text>
        </Space>
      ),
    },
    {
      title: "Etablissement",
      dataIndex: "etabName",
      key: "etabName",
      width: 180,
      sorter: (a, b) => a.etabName.localeCompare(b.etabName),
      ...getSearchFilterProps("etabName", "Rechercher par etablissement"),
      render: (value) => <Text>{value}</Text>,
    },
    {
      title: "Campagne",
      dataIndex: "campaignName",
      key: "campaignName",
      width: 180,
      sorter: (a, b) => a.campaignName.localeCompare(b.campaignName),
      ...getSearchFilterProps("campaignName", "Rechercher par campagne"),
      render: (value) => <Tag color="processing">{value}</Tag>,
    },
    {
      title: "Nature",
      dataIndex: "nature",
      key: "nature",
      width: 160,
      filters: uniqueNatures.map((n) => ({ text: n, value: n })),
      onFilter: (value, record) => record.nature === value,
      render: (value) => <Tag>{value}</Tag>,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 130,
      filters: uniqueTypes.map((t) => ({ text: t, value: t })),
      onFilter: (value, record) => record.type === value,
      render: (value) => <Tag color="blue">{value}</Tag>,
    },
    {
      title: "Valeur",
      dataIndex: "value",
      key: "value",
      width: 110,
      align: "center",
      sorter: (a, b) => safeNumber(a.value) - safeNumber(b.value),
      render: (value) => (
        <Tag color="green" icon={<MoneyCollectOutlined />}>
          {safeNumber(value)}EUR
        </Tag>
      ),
    },
    {
      title: "Cout",
      dataIndex: "cout",
      key: "cout",
      width: 110,
      align: "center",
      sorter: (a, b) => safeNumber(a.cout) - safeNumber(b.cout),
      render: (value) => (
        <Tag color="orange" icon={<DollarOutlined />}>
          {safeNumber(value)}EUR
        </Tag>
      ),
    },
    {
      title: "Nombre Distribution",
      dataIndex: "distributionCount",
      key: "distributionCount",
      width: 150,
      align: "center",
      sorter: (a, b) => safeNumber(a.distributionCount) - safeNumber(b.distributionCount),
    },
    {
      title: "Nombre Utilisation",
      dataIndex: "usageCount",
      key: "usageCount",
      width: 150,
      align: "center",
      sorter: (a, b) => safeNumber(a.usageCount) - safeNumber(b.usageCount),
    },
    {
      title: "Evaluation",
      dataIndex: "avgRating",
      key: "avgRating",
      width: 120,
      align: "center",
      sorter: (a, b) => safeNumber(a.avgRating) - safeNumber(b.avgRating),
      render: (value) => (
        <Space>
          <StarOutlined style={{ color: "#fadb14" }} />
          {safeNumber(value).toFixed(1)}
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => {
            setSelectedReward(record);
            setModalVisible(true);
          }}
        >
          Details
        </Button>
      ),
    },
  ];

  const totalDistributions = groupedRewards.reduce(
    (sum, reward) => sum + safeNumber(reward.distributionCount),
    0
  );
  const totalUsages = groupedRewards.reduce(
    (sum, reward) => sum + safeNumber(reward.usageCount),
    0
  );
  const totalValue = groupedRewards.reduce(
    (sum, reward) =>
      sum + safeNumber(reward.distributionCount) * safeNumber(reward.value),
    0
  );
  const totalCost = groupedRewards.reduce(
    (sum, reward) =>
      sum + safeNumber(reward.distributionCount) * safeNumber(reward.cout),
    0
  );

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Tableau de bord des recompenses</Title>

      {!loading && (
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Statistic
                title="Total distributions"
                value={totalDistributions}
                prefix={<GiftOutlined />}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Total utilisations"
                value={totalUsages}
                prefix={<GiftOutlined />}
                valueStyle={{ color: "#52c41a" }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Valeur totale distribuee"
                value={safeNumber(totalValue).toFixed(2)}
                prefix="EUR"
                valueStyle={{ color: "#1890ff" }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Cout total"
                value={safeNumber(totalCost).toFixed(2)}
                prefix="EUR"
                valueStyle={{ color: "#fa8c16" }}
              />
            </Col>
          </Row>
        </Card>
      )}

      <Card loading={loading}>
        {groupedRewards.length === 0 && !loading ? (
          <Empty description="Aucune recompense trouvee" />
        ) : (
          <Table
            columns={columns}
            dataSource={groupedRewards}
            rowKey="key"
            scroll={{ x: "max-content" }}
            pagination={{ pageSize: 10 }}
            locale={{
              emptyText: "Aucune recompense trouvee",
            }}
          />
        )}
      </Card>

      <RewardDetailModal
        visible={modalVisible}
        reward={selectedReward}
        onClose={() => setModalVisible(false)}
      />
    </div>
  );
};

export default RewardDashboard;
